import { type MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from 'react';

interface UseResizableWidthParams {
  /** Width (px) the panel starts at and resets to on a fresh mount. */
  initialWidth: number;
  /** Smallest width (px) the panel may shrink to. */
  minWidth: number;
  /**
   * Largest width (px) the panel may grow to. Pass a function for caps that
   * depend on runtime values (e.g. a fraction of `window.innerWidth`) — it is
   * re-evaluated on every drag frame and on window resize.
   */
  maxWidth: number | (() => number);
  /**
   * Maps a drag `mousemove` event to the candidate width (px) before clamping.
   * The caller owns the geometry so the hook works for either-edge handles —
   * e.g. a right-docked panel with a left handle uses
   * `(e) => rightEdge - e.clientX`.
   */
  computeWidth: (event: MouseEvent) => number;
}

interface UseResizableWidthResult {
  /** Current clamped width (px). */
  width: number;
  /** Resolved current maximum width (px) — refreshed on window resize for viewport-relative caps. */
  maxWidth: number;
  /** Whether a drag is currently in progress. */
  isResizing: boolean;
  /** `onMouseDown` handler to attach to the drag handle element. */
  handleMouseDown: (event: ReactMouseEvent) => void;
  /**
   * Imperatively set the width (clamped to `[minWidth, maxWidth]`) — e.g. for
   * keyboard nudges. Accepts a functional updater so successive nudges batched
   * in one render compose instead of clobbering each other.
   */
  setWidth: (width: number | ((previousWidth: number) => number)) => void;
}

/**
 * Resolves the configured max width to a number, evaluating the function form
 * (used for viewport-relative caps) on demand.
 *
 * @param {number | (() => number)} maxWidth - The configured maximum width
 * @returns {number} The current maximum width in pixels
 * @example
 * resolveMaxWidth(600); // → 600
 * resolveMaxWidth(() => window.innerWidth * 0.4); // → 40% of the viewport
 */
const resolveMaxWidth = (maxWidth: number | (() => number)): number =>
  typeof maxWidth === 'function' ? maxWidth() : maxWidth;

/**
 * Drives a drag-to-resize panel: owns the current width and drag state, wires
 * up the `window` mouse listeners for the duration of a drag, and keeps the
 * width within `[minWidth, maxWidth]` at all times.
 *
 * Responsibilities:
 * 1. Expose `handleMouseDown` to start a drag (sets the `col-resize` body cursor
 *    and suppresses text selection so the drag feels native)
 * 2. While dragging, translate each `mousemove` into a clamped width via the
 *    caller-supplied `computeWidth`, and end the drag on `mouseup`
 * 3. Re-clamp on `window` resize so a shrunk viewport can never strand the width
 *    above a viewport-relative `maxWidth`
 *
 * Width is session-only React state — it resets to `initialWidth` whenever the
 * owning component unmounts; nothing is persisted.
 *
 * @param {UseResizableWidthParams} params - Width bounds and drag geometry
 * @returns {UseResizableWidthResult} The current width, drag state, and control handlers
 * @example
 * const { width, isResizing, handleMouseDown } = useResizableWidth({
 *   initialWidth: 325,
 *   minWidth: 325,
 *   maxWidth: () => window.innerWidth * 0.4, // viewport-relative cap
 *   // right-docked panel: width grows as the left-edge handle moves left
 *   computeWidth: (event) => window.innerWidth - event.clientX,
 * });
 * return <div style={{ width }}><Handle onMouseDown={handleMouseDown} /></div>;
 */
function useResizableWidth({
  initialWidth,
  minWidth,
  maxWidth,
  computeWidth,
}: UseResizableWidthParams): UseResizableWidthResult {
  // Clamp the seed so an out-of-range initialWidth can't violate the
  // [minWidth, maxWidth] invariant on the very first render.
  const [width, setWidthState] = useState(() => Math.max(minWidth, Math.min(initialWidth, resolveMaxWidth(maxWidth))));
  const [isResizing, setIsResizing] = useState(false);
  // Resolved cap tracked in state so a viewport-relative `maxWidth` re-renders
  // consumers (e.g. for `aria-valuemax`) when the viewport changes — even when
  // the width itself stays within the new cap and does not change.
  const [resolvedMaxWidth, setResolvedMaxWidth] = useState(() => resolveMaxWidth(maxWidth));

  // Keep the latest params in refs so the drag listeners — subscribed once per
  // drag, keyed only on `isResizing` — always read current geometry without
  // re-subscribing on every parent render (which would drop mid-drag frames).
  const minWidthRef = useRef(minWidth);
  const maxWidthRef = useRef(maxWidth);
  const computeWidthRef = useRef(computeWidth);
  minWidthRef.current = minWidth;
  maxWidthRef.current = maxWidth;
  computeWidthRef.current = computeWidth;

  const clampWidth = useCallback((candidate: number): number => {
    const max = resolveMaxWidth(maxWidthRef.current);
    return Math.max(minWidthRef.current, Math.min(candidate, max));
  }, []);

  const setWidth = useCallback(
    (next: number | ((previousWidth: number) => number)) => {
      setWidthState((previousWidth) => clampWidth(typeof next === 'function' ? next(previousWidth) : next));
    },
    [clampWidth],
  );

  const handleMouseDown = useCallback((event: ReactMouseEvent) => {
    event.preventDefault();
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    setIsResizing(true);
  }, []);

  // Attach the move/up listeners only while dragging. The cleanup restores the
  // body styles, so it covers both a normal `mouseup` (isResizing → false) and
  // an unmount mid-drag without leaving a stuck `col-resize` cursor.
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      setWidthState(clampWidth(computeWidthRef.current(event)));
    };
    const stopResizing = () => setIsResizing(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, clampWidth]);

  // Re-clamp on viewport changes so a viewport-relative `maxWidth` can't be
  // exceeded after the window shrinks, and refresh the exposed cap.
  useEffect(() => {
    const handleResize = () => {
      setResolvedMaxWidth(resolveMaxWidth(maxWidthRef.current));
      setWidthState((prev) => clampWidth(prev));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampWidth]);

  return { width, maxWidth: resolvedMaxWidth, isResizing, handleMouseDown, setWidth };
}

export { useResizableWidth };
